import { Container, type Texture } from 'pixi.js';
import type { GameClient } from '../net/client';
import { MessageTag } from '../net/messageTags';
import { GameName, RoomName } from '../net/enums';
import type { Atlas } from '../assets/AtlasLoader';
import { SceneManager, type Scene } from './SceneManager';
import { Dialog, type DialogOpts } from '../ui/Dialog';
import { gameState } from '../state/GameState';
import { setTelegramBackButton } from '../platform/telegram';
import { SpectatorIntent, type GameInfoSeat } from '../net/codec/matchmaking';

/**
 * The reconnect flow's control surface, implemented by ReconnectController and assigned in main.ts.
 * The reconnect page/modal call into it (retry now / give up to Login). Typed structurally here so
 * AppContext stays free of concrete-scene/controller imports (no cycle).
 */
export interface ReconnectHandle {
  /** Force an immediate reconnect attempt (the page "تلاش مجدد" button). */
  retryNow(): void;
  /** Abandon reconnecting and return to the login screen. */
  cancelToLogin(): void;
  /** Validate or recover the session when Telegram/browser returns to the foreground. */
  handleActivation(): void;
}

/**
 * Shared services every scene needs: the network client, the scene manager, the loaded atlases, a
 * UI layer for modal dialogs, and a room→scene router. Routes are injected by main.ts so this file
 * never imports concrete scenes (no import cycle).
 */
export class AppContext {
  routes: Partial<Record<RoomName, () => Scene>> = {};
  private readonly spectatorIntent = new SpectatorIntent();

  /** The reconnect controller (set in main.ts); the reconnect page/modal drive the flow through it. */
  reconnect?: ReconnectHandle;

  constructor(
    readonly client: GameClient,
    readonly scenes: SceneManager,
    readonly profileAtlas: Atlas,
    readonly respackAtlas: Atlas | null,
    readonly uiLayer: Container,
    /** Standalone UI textures copied from Unity (coin, return, blackjack, …), keyed by file stem. */
    readonly icons: Map<string, Texture> = new Map(),
  ) {}

  avatarTexture(index: number): Texture | undefined {
    return this.profileAtlas.frames.get(`avatar_sp_${index}`);
  }

  /** A standalone UI texture (web-client/public/img/<name>.png), or undefined if it failed to load. */
  iconTexture(name: string): Texture | undefined {
    return this.icons.get(name);
  }

  /** Record the exact running table selected in Search before sending WatchGame. */
  beginSpectating(uniqueId: string): void {
    this.spectatorIntent.begin(uniqueId);
  }

  /** Combine server preview metadata with local watch intent, consuming that intent exactly once. */
  resolveTableSeat(seat: GameInfoSeat): GameInfoSeat {
    return this.spectatorIntent.resolve(seat);
  }

  /** A named frame from the respack atlas (logo, GameIcon, telegram, …), or undefined. */
  respackFrame(name: string): Texture | undefined {
    return this.respackAtlas?.frames.get(name);
  }

  /**
   * The per-game tile icon. These are now purpose-made standalone PNGs in web-client/public/img
   * (glossy wood+gold set), one per game name, replacing the Unity respack frames the lobby used to
   * borrow (HokmIcon / GameIcon). Blackjack already lived outside the atlas.
   */
  gameIcon(game: GameName): Texture | undefined {
    switch (game) {
      case GameName.Hokm:
        return this.iconTexture('hokm');
      case GameName.Backgammon:
        return this.iconTexture('backgammon');
      case GameName.Blackjack:
        return this.iconTexture('blackjack');
      default:
        return undefined;
    }
  }

  dialog(opts: DialogOpts): void {
    this.uiLayer.addChild(new Dialog(opts));
  }

  navigate(room: RoomName): void {
    const make = this.routes[room];
    if (make) {
      this.configureBackButton(room);
      this.scenes.show(make());
    }
    else console.warn('[router] no scene registered for room', RoomName[room] ?? room);
  }

  /** Configure Telegram's native header back control for the lobby navigation hierarchy. */
  private configureBackButton(room: RoomName): void {
    if (room === RoomName.Login || room === RoomName.MainMenu) {
      setTelegramBackButton(null);
      return;
    }
    const target = room === RoomName.Register || room === RoomName.Connect ? RoomName.Login : RoomName.MainMenu;
    setTelegramBackButton(() => this.navigate(target));
  }

  /** Ready and table scenes are modal/game states; they own their explicit exit confirmations. */
  hideTelegramBackButton(): void {
    setTelegramBackButton(null);
  }

  /**
   * Installs the global, server-driven handlers that exist in every scene (mirroring the Unity
   * Network.cs switch): SendMessage(0) popups, ChangeRoom(25) navigation, and Update(23) prompts.
   */
  installGlobalHandlers(): void {
    this.client.on(MessageTag.SendMessage, (r) => {
      const text = r.getString();
      if (text) this.dialog({ message: text });
    });
    this.client.on(MessageTag.ChangeRoom, (r) => {
      const room = r.getShort() as RoomName;
      if (room === RoomName.Login) gameState.reset();
      this.navigate(room);
    });
    this.client.on(MessageTag.Update, (r) => {
      const message = r.getString();
      const link = r.getString();
      this.dialog({ title: 'به‌روزرسانی نسخه', message: link ? `${message}\n${link}` : message });
    });
  }
}
